import { type FC, memo, type ReactNode } from "react";

import { Expandable } from "../../common/components/expandable/Expandable";
import { IconType } from "../../common/components/icon/IconType";
import { Link } from "../../common/components/Link";
import { Icon } from "../../common/components/icon/Icon";
import { Permission, userHasPermission } from "../../utils/permissions";
import { URL_BULK_TEMPLATE, URL_BULK_TEMPLATE_XLSX } from "../../utils/url/staticUrls";
import { userCanSeePriceReference } from "../../Cart/components/CartPage/LineItemStaticParams";
import { DateFormatType, getPattern } from "../../utils/DateFormatter";
import { useTranslation } from "react-i18next";

const Paragraph: FC<{ classList?: string; children?: ReactNode }> = ({ classList = "", children }) => (
  <p className={`scha__bulk-add__help-container__paragraph ${classList}`}>{children}</p>
);

const SectionHeader: FC<{ classList?: string; children?: ReactNode }> = ({ classList = "", children }) => (
  <h3 className={`scha__bulk-add__help-container__section-header ${classList}`}>{children}</h3>
);

const Section = ({ children }: { children?: ReactNode }) => <div>{children}</div>;

const DownloadLink: FC<{
  classList?: string;
  label: ReactNode;
  link: string;
}> = ({ classList = "", link, label }) => (
  <div className={`pl-0 ${classList}`}>
    <Link href={link} iconBefore={<Icon type={IconType.Download} />} skipResolve={true}>
      {label}
    </Link>
  </div>
);

const HelpSection: FC = () => {
  const { t } = useTranslation();

  const downloadTemplateCSV = `${URL_BULK_TEMPLATE}`;
  const downloadTemplateXLSX = `${URL_BULK_TEMPLATE_XLSX}`;

  const userHasEndCustomerPermission = userHasPermission(Permission.SEE_ENDCUSTOMER_TABULAR_VIEW);

  return (
    <div className="scha__bulk-add__help-container">
      <div className="scha__bulk-add__help-container__title">{t("bulkUpload.help.headline")}</div>
      <Expandable
        summary={t("bulkUpload.help.upload")}
        expandedDetails={
          <div className="d-flex flex-direction-column flex-1">
            <Section>
              <Paragraph classList={"my-3"}>{t("bulkUpload.help.item1.paragraph1")}</Paragraph>
              <SectionHeader>{t("bulkUpload.help.item1.header1")}</SectionHeader>
              <Paragraph>
                {t("bulkUpload.help.item1.paragraph2", {
                  bulkMaxRows: window.SCHA.config.bulkMaxRows,
                })}
              </Paragraph>
              <DownloadLink
                label={t("bulkUpload.help.item1.downloadButton1")}
                link={downloadTemplateXLSX}
                classList={"my-3"}
              />
            </Section>
            <Section>
              <Paragraph>
                {t("bulkUpload.help.item1.paragraph3", {
                  dateFormat: getPattern(DateFormatType.DATE),
                })}
              </Paragraph>
              <DownloadLink
                label={t("bulkUpload.help.item1.downloadButton2")}
                link={downloadTemplateCSV}
                classList={"my-3"}
              />
              <Paragraph classList={"mb-3"}>{t("bulkUpload.help.item1.paragraph4")}</Paragraph>
            </Section>
          </div>
        }
      />
      <Expandable
        summary={t("bulkUpload.help.text")}
        expandedDetails={
          <Section>
            <table className={"scha__bulk-add__help-container__example-table"}>
              <tbody>
                <tr className={"title-medium"}>
                  <td>0621631590000</td>
                  <td>,</td>
                  <td>100</td>
                  <td>,</td>
                  <td>{getPattern(DateFormatType.DATE)}</td>
                  <td>,</td>
                  <td>{t("bulkUpload.help.example.comment")}</td>
                  {userCanSeePriceReference() && <td>{t("bulkUpload.help.example.priceReference")}</td>}
                </tr>

                <tr className={"label-large"}>
                  <td>{t("bulkUpload.help.example.explanation.designation")}</td>
                  <td />
                  <td>{t("bulkUpload.help.example.explanation.quantity")}</td>
                  <td />
                  <td>{t("bulkUpload.help.example.explanation.rdd")}</td>
                  <td />
                  <td>{t("bulkUpload.help.example.explanation.comment")}</td>
                  {userCanSeePriceReference() && <td>{t("bulkUpload.help.example.explanation.priceReference")}</td>}
                </tr>
              </tbody>
            </table>

            <Paragraph classList={"my-3"}>
              {t("bulkUpload.help.item2.paragraph1", {
                bulkMaxRows: window.SCHA.config.bulkMaxRows,
                dateFormat: getPattern(DateFormatType.DATE),
              })}
            </Paragraph>

            <Paragraph>{t("bulkUpload.help.item2.paragraph2")}</Paragraph>
          </Section>
        }
      />
    </div>
  );
};

export default memo(HelpSection);
